import { Lock, Unlock, MapPin, Calendar, DollarSign, MessageCircle, Flag, Sparkles } from "lucide-react";
import LeadScoreBadge from "@/components/contractor/LeadScoreBadge";

/**
 * Presentational card for a single lead in the contractor dashboard.
 * Renders locked (unlock-CTA) or unlocked (contact details + chat/dispute) state.
 */
export default function LeadCard({ job, cats, onUnlock, onMessage, onDispute, onDraft }) {
  const cat = cats.find((c) => c.category_id === job.category_id);
  const price = cat?.lead_price?.toFixed(2);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="brut-card p-6 bg-white" data-testid={`lead-${job.job_id}`}>
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="overline bg-acid border-2 border-ink px-2 py-1">{job.category_name}</span>
            <span className="overline">{job.timeline}</span>
            {job.photos?.length > 0 && <span className="overline">{job.photos.length} photos</span>}
            <LeadScoreBadge job={job} />
          </div>
          <h3 className="font-display font-bold text-2xl uppercase mt-3">{job.title}</h3>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground font-mono">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location}, {job.zip_code}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(job.created_at).toLocaleDateString()}</span>
            {job.budget_min && (
              <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${job.budget_min}-${job.budget_max}</span>
            )}
          </div>
          <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{job.description}</p>
          {job.photos?.length > 0 && (
            <div className="mt-3 flex gap-2">
              {job.photos.slice(0, 4).map((p) => (
                <img key={p} src={`${backendUrl}/api/files/${p}`} alt="" className="w-16 h-16 object-cover border-2 border-ink" />
              ))}
            </div>
          )}
        </div>
        <div className="text-right min-w-[200px]">
          {job.unlocked ? (
            <>
              <div className="brut-card p-4 bg-acid">
                <Unlock className="w-5 h-5 mb-2" />
                <div className="overline">Contact</div>
                <div className="font-mono text-xs mt-1">{job.customer_name}</div>
                <div className="font-mono text-xs">{job.customer_email}</div>
                <div className="font-mono text-xs">{job.customer_phone || "—"}</div>
              </div>
              <button onClick={onDraft} className="brut-btn-acid w-full mt-3 text-[10px]" data-testid={`draft-btn-${job.job_id}`}>
                <Sparkles className="w-3 h-3 mr-1" /> Draft reply with AI
              </button>
              <div className="mt-2 flex gap-2">
                <button onClick={onMessage} className="brut-btn flex-1 text-[10px]" data-testid={`msg-btn-${job.job_id}`}>
                  <MessageCircle className="w-3 h-3 mr-1" /> Chat
                </button>
                <button onClick={onDispute} className="brut-btn flex-1 text-[10px]" data-testid={`dispute-btn-${job.job_id}`}>
                  <Flag className="w-3 h-3 mr-1" /> Bad lead
                </button>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => onUnlock(job)} className="brut-btn-primary w-full" data-testid={`unlock-${job.job_id}`}>
                <Lock className="w-4 h-4 mr-1" /> Unlock — ${price}
              </button>
              <div className="font-mono text-[10px] mt-2 text-muted-foreground">
                {job.contractor_unlocks} other pros notified
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
